// @flow
/** This component is ported from the client's PredefinedList component, with apply values removed */
import React, { Component } from 'react';
import { type IntlShape, FormattedMessage } from 'react-intl';
import { type FieldDefinition } from 'state/types/catalogTypes';
import { cloneDeep } from 'lodash';

import { propsForSingleButtonAlert } from 'portfolio-shared/state/reducers/modalPromptReducer';
import localizedStrings from 'util/localizedStrings';
import {
  flattenNestedValues,
  nestedValuesToArray,
  parseList,
} from 'util/nestedValues';
import PDLSubTree from 'portfolio-shared/view/Field/PDLSubTree';
import { isCharacterLegal, normalizeValue } from 'util/fieldsPageUtils';
import { sortValuesForFieldDef } from 'portfolio-shared/util/fieldDefUtils';
import Listbox from 'portfolio-shared/view/Utilities/Listbox';

const IS_WINDOWS = window.navigator.appVersion.includes('Windows');

type Props = {
  editPDL: Function,
  fieldDef: FieldDefinition,
  intl: IntlShape,
  reduxFormInput: Object,
  showModalPrompt: Function,
  submitFieldValue: Function,
};

type State = {
  selectedPDLItems: Set<any>,
  predefValueInput: string,
};

export default class PredefinedList extends Component<Props, State> {
  addSelectedPDLItems: Function;
  addValueToPDL: Function;
  exportPDL: Function;
  findLeafmostSelectedNestedValues: Function;
  getLastSelectedNestedValue: Function;
  handleKeyPress_editPredefinedValuesInput: Function;
  handleKeyUp_editPredefinedValuesInput: Function;
  handleSelectFileForImportPDL: Function;
  importPDL: Function;
  normalizer: Function;
  onChangeValuesListSelection: Function;
  onKeyPressPredefinedList: Function;
  onPredefValueChange: Function;
  removePDLValues: Function;
  removeSelectedPDLItems: Function;
  removeSelectedValuesFromPDL: Function;
  removeSelectedValuesFromPDLList: Function;
  removeSelectedValuesFromPDLTree: Function;
  renderValuesTree: Function;
  setSelectedPDLItems: Function;
  submitFieldValue: Function;

  fileInput: any;
  pdlListbox: any;
  editPredefInput: any;

  constructor() {
    super();
    this.state = {
      selectedPDLItems: new Set(),
      predefValueInput: '',
    };
    this.addSelectedPDLItems = this.addSelectedPDLItems.bind(this);
    this.addValueToPDL = this.addValueToPDL.bind(this);

    this.exportPDL = this.exportPDL.bind(this);
    this.findLeafmostSelectedNestedValues = this.findLeafmostSelectedNestedValues.bind(
      this
    );
    this.getLastSelectedNestedValue = this.getLastSelectedNestedValue.bind(
      this
    );
    this.handleKeyPress_editPredefinedValuesInput = this.handleKeyPress_editPredefinedValuesInput.bind(
      this
    );
    this.handleKeyUp_editPredefinedValuesInput = this.handleKeyUp_editPredefinedValuesInput.bind(
      this
    );
    this.handleSelectFileForImportPDL = this.handleSelectFileForImportPDL.bind(
      this
    );
    this.importPDL = this.importPDL.bind(this);
    this.normalizer = this.normalizer.bind(this);
    this.onChangeValuesListSelection = this.onChangeValuesListSelection.bind(
      this
    );
    this.onKeyPressPredefinedList = this.onKeyPressPredefinedList.bind(this);
    this.onPredefValueChange = this.onPredefValueChange.bind(this);
    this.removePDLValues = this.removePDLValues.bind(this);
    this.removeSelectedPDLItems = this.removeSelectedPDLItems.bind(this);
    this.removeSelectedValuesFromPDL = this.removeSelectedValuesFromPDL.bind(
      this
    );
    this.removeSelectedValuesFromPDLList = this.removeSelectedValuesFromPDLList.bind(
      this
    );
    this.removeSelectedValuesFromPDLTree = this.removeSelectedValuesFromPDLTree.bind(
      this
    );
    this.renderValuesTree = this.renderValuesTree.bind(this);
    this.setSelectedPDLItems = this.setSelectedPDLItems.bind(this);
    this.submitFieldValue = this.submitFieldValue.bind(this);
  }

  setSelectedPDLItems(items: any) {
    this.setState({ selectedPDLItems: new Set(items) });
  }

  addSelectedPDLItems(items: any) {
    const newVals = new Set([...this.state.selectedPDLItems]);
    items.forEach(item => newVals.add(item));
    this.setState({ selectedPDLItems: newVals });
  }

  removeSelectedPDLItems(items: any) {
    const newVals = new Set([...this.state.selectedPDLItems]);
    items.forEach(item => newVals.delete(item));
    this.setState({ selectedPDLItems: newVals });
  }

  submitFieldValue(value: any) {
    // The reduxFormInput prop is passed in by FieldEditorPDL, but not in
    // other places that PredefinedList is used. So, pass it in as a second
    // argument if it's there.
    const { submitFieldValue, reduxFormInput } = this.props;
    return submitFieldValue(value, reduxFormInput);
  }

  onChangeValuesListSelection() {
    this.setState({
      selectedPDLItems: new Set(this.pdlListbox.getSelectedValues()),
    });
  }

  onKeyPressPredefinedList(e: any) {
    if (e.key === 'Delete' || e.key === 'Backspace') {
      this.removeSelectedValuesFromPDL();
      e.preventDefault();
    }
  }

  renderValuesList(values: any) {
    const { fieldDef } = this.props;
    sortValuesForFieldDef(fieldDef, values);

    return (
      <Listbox
        className="pdl-select"
        testName="pdl-flat"
        ref={input => {
          this.pdlListbox = input;
        }}
        isMultiple={fieldDef.isMultivalued}
        onChange={this.onChangeValuesListSelection}
        onKeyDown={this.onKeyPressPredefinedList}
        listItems={values}
        initialSize={6}
      />
    );
  }

  renderValuesTree(values: any) {
    return (
      <div className="pdl-tree" data-test-name="pdl-tree">
        {values
          .sort((a, b) => a.value.localeCompare(b.value))
          .map(value => (
            <PDLSubTree
              ancestorValues={[value]}
              itemSelectedStyle="pdl-item-selected"
              itemStyle="pdl-item"
              key={`PDL-${value.id}`}
              level={0}
              selectedPDLItems={this.state.selectedPDLItems}
              setSelectedPDLItems={this.setSelectedPDLItems}
              addSelectedPDLItems={this.addSelectedPDLItems}
              removeSelectedPDLItems={this.removeSelectedPDLItems}
              submitFieldValue={this.submitFieldValue}
              tree={value}
            />
          ))}
      </div>
    );
  }

  isDuplicateHierarchicalValue(value: any) {
    const { fieldDef } = this.props;
    let result = false;
    if (fieldDef.isHierarchical) {
      // selectedPDLItems has the state of the items when they were selected,
      // but that may not be an accurate picture of the children nodes if children have been added
      // and the selection has not changed.
      // So we walk the fieldDef.nestedPredefinedValues (which has all the latest changes) finding
      // our selectedNestedValue nodes until we get its peers, then we look for dupes.
      const selectedNestedValues = [...this.state.selectedPDLItems];
      let nodes = fieldDef.nestedPredefinedValues;
      for (let idx = 0; idx < selectedNestedValues.length; idx++) {
        if (idx === 0) {
          nodes = fieldDef.nestedPredefinedValues.filter(
            nv => nv.id === selectedNestedValues[idx].id
          );
        } else {
          nodes = nodes[0].children.filter(
            nv => nv.id === selectedNestedValues[idx].id
          );
        }
      }

      if (nodes && nodes.length > 0) {
        const peers =
          selectedNestedValues.length === 0 ? nodes : nodes[0].children;
        if (
          peers.some(
            nv => nv.value.toLowerCase() === value[0].value.toLowerCase()
          )
        ) {
          result = true; // dupe found
        }
      }
    }
    return result;
  }

  addValueToPDL() {
    const { fieldDef } = this.props;
    let value = this.editPredefInput.value;
    value = value ? value.trim() : value;
    if (value) {
      value = fieldDef.isHierarchical ? [{ value }] : [value];
      const parentNode = fieldDef.isHierarchical
        ? this.getLastSelectedNestedValue()
        : null;
      if (fieldDef.isHierarchical && this.isDuplicateHierarchicalValue(value)) {
        return; // early return, found a duplicate
      }
      const parentNodeId = parentNode && parentNode.id;
      this.props.editPDL(true, fieldDef, value, parentNodeId);
      this.setState({ predefValueInput: '' });
    }
  }

  getLastSelectedNestedValue() {
    const nvs = this.findLeafmostSelectedNestedValues();
    const result = nvs[nvs.length - 1];
    return result;
  }

  // Return any selected NVs which don't have children among the selected NVs
  findLeafmostSelectedNestedValues() {
    const selectedNestedValues = [...this.state.selectedPDLItems];
    const selectedNestedValueIds = selectedNestedValues.map(val => val.id);
    const leafmostNodes = selectedNestedValues.filter(
      nv =>
        !nv.children ||
        nv.children.every(child => !selectedNestedValueIds.includes(child.id))
    );
    return leafmostNodes;
  }

  removeSelectedValuesFromPDL() {
    const { fieldDef } = this.props;
    if (fieldDef.isHierarchical) {
      this.removeSelectedValuesFromPDLTree();
    } else {
      this.removeSelectedValuesFromPDLList();
    }
  }

  removeSelectedValuesFromPDLList() {
    this.removePDLValues(this.pdlListbox.getSelectedValues());
  }

  removeSelectedValuesFromPDLTree() {
    const { showModalPrompt } = this.props;
    // We just want the leaf-most NVs, not the ancestors
    // So filter out any NVs which have any children among the selected NVs
    const nestedValuesToRemove = this.findLeafmostSelectedNestedValues();
    const valueIds = nestedValuesToRemove.map(nestedValue => nestedValue.id);
    const doRemove = () => {
      // remove from server
      this.removePDLValues(valueIds);
      // remove from this state's selected PDL items
      this.removeSelectedPDLItems(nestedValuesToRemove);
    };
    // Require confirmation if there are children
    const anyValuesToRemoveHaveChildren = nestedValuesToRemove.some(
      nv => nv.children && nv.children.length > 0
    );
    if (anyValuesToRemoveHaveChildren) {
      showModalPrompt({
        message: localizedStrings.PDL_removeNestedValueConfirmation,
        onButton2: doRemove,
        closeOnEscape: true,
        isButton2Default: true,
      });
    } else {
      doRemove();
    }
  }

  removePDLValues(values: any) {
    const { fieldDef } = this.props;
    if (values && values.length > 0) {
      this.props.editPDL(false, fieldDef, values);
    }
  }

  handleKeyPress_editPredefinedValuesInput(e: any, f: any) {
    if (e.key === 'Enter') {
      this.addValueToPDL();
      e.preventDefault();
    } else if (!isCharacterLegal(e.key, this.props.fieldDef, e.target)) {
      e.preventDefault();
    }
  }

  handleKeyUp_editPredefinedValuesInput(e: any) {
    if (e.key === 'Escape') {
      e.preventDefault();
      this.setState({ predefValueInput: '' });
    }
  }

  onPredefValueChange(e: any) {
    const normalized = this.normalizer(
      e.target.value,
      this.state.predefValueInput
    );
    this.setState({ predefValueInput: normalized });
  }

  normalizer(value: any, previousValue: any) {
    return normalizeValue(this.props.fieldDef, value, previousValue);
  }

  renderNoPredefinedValues() {
    return (
      <div className="pdl-select-no-values">
        <FormattedMessage {...localizedStrings.PDL_noPredefinedValues} />
      </div>
    );
  }

  nullIfEmpty(array: any) {
    return array && array.length === 0 ? null : array;
  }

  exportPDL() {
    const { fieldDef } = this.props;
    const filename = fieldDef.systemName + '.txt';
    const returnChar = IS_WINDOWS ? '\r\n' : '\n';
    let content = '';

    if (fieldDef.isHierarchical) {
      const nestedValues = cloneDeep(fieldDef.nestedPredefinedValues);
      const flatNestedValues = flattenNestedValues(nestedValues, 0);
      content = nestedValuesToArray(flatNestedValues, returnChar);
    } else {
      content = fieldDef.predefinedValues.map(value => value + returnChar);
    }

    const file = new Blob(content, { type: 'text/plain' });

    if (window.navigator.msSaveOrOpenBlob) {
      // IE and Edge
      window.navigator.msSaveOrOpenBlob(file, filename);
    } else {
      const el = document.createElement('a');
      document.body && document.body.appendChild(el); // attach to DOM for Firefox

      el.href = URL.createObjectURL(file);
      el.download = filename;
      el.click();
    }
  }

  importPDL(e: any) {
    this.fileInput.click();
    e.preventDefault();
  }

  handleSelectFileForImportPDL(e: any) {
    const { editPDL, fieldDef, showModalPrompt } = this.props;
    const files = this.fileInput.files;
    const reader = new FileReader();
    const filename = files[0].name.toLowerCase();
    // Browsers will correctly restrict the selection to text files, but unfortunately,
    // the version of electron we are currently using (1.7.10) does not, so we need
    // to do this check.
    if (!filename.endsWith('.txt') && !filename.endsWith('.text')) {
      showModalPrompt(
        propsForSingleButtonAlert(localizedStrings.PDL_wrongPdlImportFormat)
      );
    } else {
      reader.onload = function() {
        const text = reader.result;
        const results = parseList(text, fieldDef);
        editPDL(true, fieldDef, results);
      };
      reader.readAsText(files[0]);
    }
    // reset file input so user can reselect same file if they want
    this.fileInput.value = null;
  }

  render() {
    const {
      fieldDef,
      intl: { formatMessage },
      editPDL,
    } = this.props;
    const listValues = this.nullIfEmpty(fieldDef.predefinedValues);
    const nestedValues = this.nullIfEmpty(fieldDef.nestedPredefinedValues);

    // 'no predefined values' state
    if (!editPDL && !listValues && !nestedValues) {
      return this.renderNoPredefinedValues();
    }

    return (
      <div className="box" data-test-name="pdl">
        <div className="subtitle">
          <FormattedMessage
            {...localizedStrings.FIELD_FORM_LABEL_PREDEFINED_VALUES}
          />
        </div>
        {listValues && this.renderValuesList(listValues)}
        {nestedValues && this.renderValuesTree(nestedValues)}
        {!listValues && !nestedValues && this.renderNoPredefinedValues()}

        <div key="managePredefs" className="pdl-field-widgets">
          <input
            className="input"
            type="text"
            style={{ flex: 2, marginRight: '4px', minWidth: '50px' }}
            onKeyPress={this.handleKeyPress_editPredefinedValuesInput}
            onKeyUp={this.handleKeyUp_editPredefinedValuesInput}
            onChange={this.onPredefValueChange}
            maxLength={1000}
            ref={input => {
              this.editPredefInput = input;
            }}
            value={this.state.predefValueInput}
            placeholder={formatMessage(
              localizedStrings.PDL_editPredefinedListPlaceholder
            )}
            data-test-name="edit-predefined-list-input"
          />
          <a
            className="button is-primary is-inverted"
            key="add-predefined-value"
            data-test-name="add-predefined-value"
            onClick={this.addValueToPDL}
          >
            <i className="fa fa-plus" />
          </a>
          <a
            className="button is-primary is-inverted"
            key="remove-predefined-value"
            data-test-name="remove-predefined-value"
            onClick={this.removeSelectedValuesFromPDL}
          >
            <i className="fa fa-minus" />
          </a>
          <a
            className="button is-primary is-inverted"
            key="export-predefined-values"
            data-test-name="export-predefined-values"
            title={formatMessage(localizedStrings.PDL_exportPredefinedValues)}
            onClick={this.exportPDL}
          >
            <i className="fa fa-download" />
          </a>
          <a
            className="button is-primary is-inverted"
            key="import-predefined-values"
            data-test-name="import-predefined-values"
            title={formatMessage(localizedStrings.PDL_importPredefinedValues)}
            onClick={this.importPDL}
          >
            <i className="fa fa-upload" />
          </a>
          <input
            accept=".txt, .text"
            ref={c => {
              this.fileInput = c;
            }}
            type="file"
            style={{ display: 'none' }}
            onChange={this.handleSelectFileForImportPDL}
          />
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/view/Pages/CatalogsPage/PredefinedList.js